import { ReactNode, Suspense, lazy } from 'react';
import useFreeMaterialBtnConfig from '../../common/get-free-material-button/hooks/use-free-material-btn-config';
import { BUTTON_TYPES, BUTTON_VARIANTS } from '../../constants/button-variants';
import translation from '../../languages';
import Button from '../../ui/button/button';
import SharedModal from '../../ui/modal/shared-modal';
import { SharedModalButtonConfig } from '../../ui/modal/shared-modal.types';
import Icon from '../icon/icon';
import './get-free-material-btn.scss';

const FreeDownloadModal = lazy(
    () => import(/* webpackChunkName: "free-download-modal" */ '../free-download-modal/free-download-modal')
);

export type GetFreeMaterialButtonConfigProps = {
    showModal: boolean;
    hideModal: VoidFunction;
    showFreeModal: boolean;
    hideFreeModal: VoidFunction;
    getMaterial: VoidFunction;
    modalDescription: string;
    buttonText: string;
    buttonConfig: SharedModalButtonConfig;
    modalClassName?: string;
    modalDescriptionLink?: string;
};

type GetFreeMaterialButtonProps = TranslationProps & {
    material: Material;
    type: BUTTON_TYPES;
    onModalClose?: () => void;
    className?: string;
    iconType?: string;
    children?: ReactNode;
    variant?: BUTTON_VARIANTS;
    onBeforeGetMaterial?: VoidFunction;
    searchRequestId?: string;
};

const GetFreeMaterialButton = (props: GetFreeMaterialButtonProps) => {
    const {
        material,
        type,
        children,
        iconType = 'plus',
        variant,
        onModalClose = () => {},
        className,
        onBeforeGetMaterial,
        searchRequestId,
        t
    } = props;
    const { getMaterial, modalDescriptionLink, buttonText, modalDescription, ...config } = useFreeMaterialBtnConfig({
        material,
        t,
        onModalClose,
        searchRequestId
    });

    const modalDescriptionToUse = modalDescriptionLink
        ? modalDescription.replace(/<a href='#'>/, `<a href='${modalDescriptionLink}'>`)
        : modalDescription;

    const handleClick = onBeforeGetMaterial ?? getMaterial;

    return (
        <span className={`free-material-download-btn ${className}`}>
            {type === BUTTON_TYPES.ICON && <Icon name={iconType} onClick={handleClick} />}
            {type === BUTTON_TYPES.BUTTON && (
                <Button className='plus-button' variant={variant || BUTTON_VARIANTS.YELLOW} onClick={handleClick}>
                    {children || buttonText}
                </Button>
            )}
            {type === BUTTON_TYPES.ICON_BUTTON && (
                <Button className='plus-button' variant={variant || BUTTON_VARIANTS.SECONDARY} onClick={handleClick}>
                    <>
                        <Icon className='mr-3' name='plus' onClick={getMaterial} />
                        {buttonText}
                    </>
                </Button>
            )}
            {config.showModal && (
                <SharedModal
                    dialogClassName={`free-material-modal ${config.modalClassName}`}
                    body={
                        <div className='text-center tsc-h4 mb-4 ml-3 mr-3'>
                            <span dangerouslySetInnerHTML={{ __html: modalDescriptionToUse }} />
                        </div>
                    }
                    btnConfig={config.buttonConfig}
                    title={<></>}
                    onClose={config.hideModal}
                />
            )}
            {config.showFreeModal && (
                <Suspense fallback={<div />}>
                    <FreeDownloadModal materialId={material.id} onClose={config.hideFreeModal} />
                </Suspense>
            )}
        </span>
    );
};

const translate = translation(['common', 'material-page', 'header', 'cabinet-materials']);

export default translate(GetFreeMaterialButton);
